import type { ParticipantRestrictivePractice } from '@piccolohealth/pbs-common';
import { ChakraV3, DescriptionList, FancyDate } from '@piccolohealth/ui';
import { DateTime } from '@piccolohealth/util';
import React from 'react';
import { ManageParticipantRestrictivePracticeLabelsControl } from './ManageParticipantRestrictivePracticeLabelsControl';
import { ParticipantRestrictivePracticeActionMenu } from './ParticipantRestrictivePracticeActionMenu';
import { RestrictivePracticeStatusTag } from './RestrictivePracticeStatusTag';
import { restrictivePracticeTypeOptions } from './RestrictivePracticeTypeTag';

interface Props {
  participantId: string;
  participantRestrictivePractice: ParticipantRestrictivePractice;
  isDisabled?: boolean;
}

export const ParticipantRestrictivePracticeCard = (props: Props) => {
  const { participantId, participantRestrictivePractice, isDisabled } = props;

  const text = restrictivePracticeTypeOptions[participantRestrictivePractice.type].label;

  const identifiedAt = DateTime.fromISO(participantRestrictivePractice.identifiedAt.toString());
  const startedAt = participantRestrictivePractice.startedAt
    ? DateTime.fromISO(participantRestrictivePractice.startedAt.toString())
    : null;
  const ceasedAt = participantRestrictivePractice.ceasedAt
    ? DateTime.fromISO(participantRestrictivePractice.ceasedAt.toString())
    : null;

  const items = [
    {
      title: 'Description',
      content: <ChakraV3.Text>{participantRestrictivePractice.description}</ChakraV3.Text>,
    },
    {
      title: 'Date Identified',
      content: (
        <FancyDate
          date={identifiedAt}
          format={DateTime.DATE_SHORT}
          showHumanized={false}
          direction='row'
        />
      ),
    },
    {
      title: 'Date Started',
      content: startedAt ? (
        <FancyDate
          date={startedAt}
          format={DateTime.DATE_SHORT}
          showHumanized={false}
          direction='row'
        />
      ) : (
        <ChakraV3.Text>N/A</ChakraV3.Text>
      ),
    },
    {
      title: 'Date Ceased',
      content: ceasedAt ? (
        <FancyDate
          date={ceasedAt}
          format={DateTime.DATE_SHORT}
          showHumanized={false}
          direction='row'
        />
      ) : (
        <ChakraV3.Text>Still ongoing</ChakraV3.Text>
      ),
    },

    {
      title: 'Labels',
      content: (
        <ChakraV3.Stack ml={-4}>
          <ManageParticipantRestrictivePracticeLabelsControl
            participantId={participantId}
            restrictivePracticeId={participantRestrictivePractice.id}
            value={participantRestrictivePractice.labels.map((l) => l.id)}
            isDisabled={isDisabled}
          />
        </ChakraV3.Stack>
      ),
    },
  ];

  return (
    <ChakraV3.Card.Root w='full' h='full'>
      <ChakraV3.Card.Header>
        <ChakraV3.Stack>
          <ChakraV3.HStack align='stretch'>
            <ChakraV3.Stack gap={0}>
              <ChakraV3.Heading size='xl' lineClamp={1}>
                {text}
              </ChakraV3.Heading>
            </ChakraV3.Stack>
            <ChakraV3.Spacer />
            <ParticipantRestrictivePracticeActionMenu
              participantId={participantId}
              participantRestrictivePractice={participantRestrictivePractice}
              isDisabled={isDisabled}
            />
          </ChakraV3.HStack>
          <RestrictivePracticeStatusTag
            status={participantRestrictivePractice.status}
            w='fit-content'
          />
        </ChakraV3.Stack>
      </ChakraV3.Card.Header>

      <ChakraV3.Card.Body>
        <ChakraV3.Stack gap={4} h='full'>
          <DescriptionList items={items} fontSize='sm' />
        </ChakraV3.Stack>
      </ChakraV3.Card.Body>
    </ChakraV3.Card.Root>
  );
};
